<script setup>
    import Store from '../store';
    import { FilterMatchMode, FilterOperator } from 'primevue/api';
    import DataTable from 'primevue/datatable';
    import TriStateCheckbox from 'primevue/tristatecheckbox';
    import InputText from 'primevue/inputtext';
    import Button from 'primevue/button';
    import Column from 'primevue/column';
    import Dialog from 'primevue/dialog';
    import Password from 'primevue/password';
    import Checkbox from 'primevue/checkbox';
    import Textarea from 'primevue/textarea';
    import Message from 'primevue/message';
    import { useConfirm } from "primevue/useconfirm";
    import { ref, onBeforeMount, onMounted } from 'vue';
    import api from '../lib/serverApi';
    import dialogs from '../lib/dialogs';
    import TabView from 'primevue/tabview';
    import TabPanel from 'primevue/tabpanel';

    const confirm = useConfirm();

    const userList = ref(null);
    const filters = ref(null);
    const loading = ref(false);
    const userDialogVisible = ref(false);
    const passwordDialogVisible = ref(false);
    const editUserData = ref({
        dialogMode: '',
        dialogCaption: '',
        login: '',
        loginInvalid: '',
        isAdmin: false,
        displayName: '',
        email: '',
        photo: '',
        entitiePermissions: '',
        dasboardPermissions: '',
        password: '',
        passwordInvalid: '',
        passwordСonfirm: '',
        passwordСonfirmInvalid: '',
    });
    const selectionUser = ref(null);

    onBeforeMount(() => {
        Store.commit('SET_FORM_HEADER', 'Администрирование > Пользователи');
        initFilters();
    });

    onMounted(() => {
        loadUserList();
    });

    async function loadUserList() {
        loading.value = true;
        try {
            userList.value = await api.getUserList();
        } catch(error) {
            dialogs.showException(error);
        }
        loading.value = false;
    }

    const initFilters = () => {
        filters.value = {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            login: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            displayName: { value: null, matchMode: FilterMatchMode.IN },
            isAdmin: { value: null, matchMode: FilterMatchMode.EQUALS }
        };
    }

    function onAddButton() {
        editUserData.value.dialogMode = 'ADD';
        editUserData.value.dialogCaption = 'Добавить пользователя';

        editUserData.value.login = '';
        editUserData.value.loginInvalid = '';
        editUserData.value.isAdmin = false;
        editUserData.value.displayName = '';
        editUserData.value.email = '';
        editUserData.value.photo = '';
        editUserData.value.entitiePermissions = '';
        editUserData.value.dasboardPermissions = '';
        editUserData.value.password = '';
        editUserData.value.passwordInvalid = '';
        editUserData.value.passwordСonfirm = '';
        editUserData.value.passwordСonfirmInvalid = '';

        userDialogVisible.value = true;
    }

    function onChangeButton() {
        if (!selectionUser.value) return;

        editUserData.value.dialogMode = 'CHANGE';
        editUserData.value.dialogCaption = 'Редактирование пользователя';

        editUserData.value.login = selectionUser.value.login;
        editUserData.value.loginInvalid = '';
        editUserData.value.isAdmin = selectionUser.value.isAdmin;
        editUserData.value.displayName = selectionUser.value.displayName;
        editUserData.value.email = selectionUser.value.email;
        editUserData.value.photo = selectionUser.value.photo;
        if (selectionUser.value.permissions.entities)
            editUserData.value.entitiePermissions = selectionUser.value.permissions.entities.join('\n');
        else editUserData.value.entitiePermissions = '';
        if (selectionUser.value.permissions.dashboards)
            editUserData.value.dasboardPermissions = selectionUser.value.permissions.dashboards.join('\n');
        else editUserData.value.dasboardPermissions = '';

        editUserData.value.password = '';
        editUserData.value.passwordInvalid = '';
        editUserData.value.passwordСonfirm = '';
        editUserData.value.passwordСonfirmInvalid = '';

        userDialogVisible.value = true;
    }

    function onDeleteButton() {
        if (!selectionUser.value) return;

        confirm.require({
            header: 'Подтверждение',
            message: `Вы действительно хотите удалить пользователя "${selectionUser.value.login}"`,
            icon: 'pi pi-exclamation-triangle',
            rejectLabel: 'Да',
            rejectClass: 'p-button-danger',
            acceptLabel: 'Нет',
            acceptClass: '',
            reject: () => {
                api.deleteUser(selectionUser.value.login)
                .then(() => loadUserList())
                .catch(error => dialogs.showException(error));
            },
        });
    }

    function onUserDialogOk() {
        if (editUserData.value.dialogMode === 'ADD') addUser();
        if (editUserData.value.dialogMode === 'CHANGE') changeUser();
    }

    async function addUser() {
        if (editUserData.value.login.length === 0) {
            editUserData.value.loginInvalid = 'p-invalid';
            return;
        } else editUserData.value.loginInvalid = '';

        if (editUserData.value.password.length === 0) {
            editUserData.value.passwordInvalid = 'p-invalid';
            return;
        } else editUserData.value.passwordInvalid = '';

        if (editUserData.value.password !== editUserData.value.passwordСonfirm) {
            editUserData.value.passwordInvalid = 'p-invalid';
            editUserData.value.passwordСonfirmInvalid = 'p-invalid';
            return;
        } else editUserData.value.passwordСonfirmInvalid = '';

        try {
            const res = await api.addNewUser(editUserData.value.login, {
                isAdmin: editUserData.value.isAdmin || false,
                displayName: editUserData.value.displayName,
                email: editUserData.value.email,
                photo: editUserData.value.photo,
                permissions: {
                    entities: editUserData.value.entitiePermissions.split('\n'),
                    dashboards: editUserData.value.dasboardPermissions.split('\n')
                },
                password: editUserData.value.password,
            })

            if (!res) throw new Error('Не удалось добавить нового пользователя');
            loadUserList();
        } catch(error) {
            dialogs.showException(error);
        }
        userDialogVisible.value = false;
    }

    async function changeUser() {
        if (editUserData.value.password.length > 0 && editUserData.value.password !== editUserData.value.passwordСonfirm) {
            editUserData.value.passwordInvalid = 'p-invalid';
            editUserData.value.passwordСonfirmInvalid = 'p-invalid';
            return;
        }

        try {
            const res = await api.changeUserData(editUserData.value.login, {
                displayName: editUserData.value.displayName,
                email: editUserData.value.email,
                photo: editUserData.value.photo,
                permissions: {
                    entities: editUserData.value.entitiePermissions.split('\n'),
                    dashboards: editUserData.value.dasboardPermissions.split('\n'),
                },
                password: editUserData.value.password,
            })

            if (!res) throw new Error('Не удалось изменить данные пользователя');
            loadUserList();
        } catch(error) {
            dialogs.showException(error);
        }
        userDialogVisible.value = false;
    }

    function onUserDialogCancel() {
        userDialogVisible.value = false;
    }

    function onChangePasswordButton() {
        if (!selectionUser.value) return;
        editUserData.value.login = selectionUser.value.login;

        editUserData.value.password = '';
        editUserData.value.passwordInvalid = '';
        editUserData.value.passwordСonfirm = '';

        passwordDialogVisible.value = true;
    }

    async function onPasswordDialogOk() {
        editUserData.value.passwordInvalid = '';
        if (editUserData.value.password.length === 0 || editUserData.value.password !== editUserData.value.passwordСonfirm) {
            editUserData.value.passwordInvalid = 'p-invalid';
            return;
        }

        try {
            const res = await api.changeUserPassword(editUserData.value.login, editUserData.value.password);

            if (res) dialogs.showInfo(`Успешно изменен пароль для пользователя "${editUserData.value.login}"`);
            else throw new Error('Не удалось изменить данные пользователя');
        } catch(error) {
            dialogs.showException(error);
        }

        passwordDialogVisible.value = false;
    }

    function onPasswordDialogCancel() {
        passwordDialogVisible.value = false;
    }

</script>

<template>
    <div class="formPanel">
        <div class="mb-2">
            <Button icon="pi pi-plus" label="Добавить" @click="onAddButton()" class="mr-1" />
            <Button icon="pi pi-pencil" label="Изменить" @click="onChangeButton()" class="mx-1" />
            <Button icon="pi pi-trash" label="Удалить" @click="onDeleteButton()" class="mx-1" severity="danger" />
            <Button icon="pi pi-lock" label="Сменить пароль" @click="onChangePasswordButton()" class="ml-5" severity="warning" />
        </div>
        <DataTable
            :value="userList"
            :paginator="true"
            stripedRows
            v-model:selection="selectionUser"
            selectionMode="single"
            class="p-datatable-sm"
            :rows="10"
            dataKey="login"
            :rowHover="true"
            v-model:filters="filters"
            filterDisplay="menu"
            :loading="loading"
            :filters="filters"
            responsiveLayout="scroll"
            :globalFilterFields="['login', 'displayName', 'isAdmin']"
        >
            <template #header>
                <div class="flex justify-content-between flex-column sm:flex-row">
                    <Button type="button" icon="pi pi-filter-slash" label="Очистить фильтр" class="p-button-outlined" @click="initFilters()" />
                    <span class="p-input-icon-left" style="width: 300px">
                        <i class="pi pi-search" />
                        <InputText v-model="filters['global'].value" placeholder="текст поиска" style="width: 100%" />
                    </span>
                </div>
            </template>
            <template #empty> Нет данных для отображения. </template>
            <template #loading> Загрузка данных. Пожалуйста подождите. </template>
            <Column field="login" header="Имя для входа" style="min-width: 12rem">
                <template #body="{ data }">
                    {{ data.login }}
                </template>
                <template #filter="{ filterModel }">
                    <InputText type="text" v-model="filterModel.value" class="p-column-filter" placeholder="Поиск по имени входа" />
                </template>
            </Column>
            <Column field="displayName" header="Описание" style="min-width: 12rem">
                <template #body="{ data }">
                    <img :src="data.photo || '/noname.png'" width="32" style="vertical-align: middle" />
                    <span style="margin-left: 0.5em; vertical-align: middle" class="image-text">{{ data.displayName }}</span>
                </template>
                <template #filter="{ filterModel }">
                    <InputText type="text" v-model="filterModel.value" class="p-column-filter" placeholder="Поиск по описанию" />
                </template>
            </Column>
            <Column field="email" header="E-mail" style="min-width: 12rem">
                <template #body="{ data }">
                    {{ data.email }}
                </template>

            </Column>
            <Column field="isAdmin" header="Администратор" dataType="boolean" bodyClass="text-center" style="min-width: 8rem">
                <template #body="{ data }">
                    <i class="pi" :class="{ 'pi-check': data.isAdmin, 'pi-minus': !data.isAdmin }"></i>
                </template>
                <template #filter="{ filterModel }">
                    <TriStateCheckbox v-model="filterModel.value" />
                </template>
            </Column>
        </DataTable>
    </div>

    <Dialog v-model:visible="userDialogVisible" modal header="Header" style="width: 60rem;">
        <template #header>
            <span class="font-bold white-space-nowrap">{{ editUserData.dialogCaption }}</span>
        </template>

        <TabView class="mt-2" style="height: 65vh;">
            <TabPanel header="Основные">
                <div class="p-fluid px-4">
                    <div class="field grid">
                        <label class="col-12 md:col-4 md:mb-0">Имя для входа</label>
                        <div class="col-12 md:col-8">
                            <InputText v-model="editUserData.login" type="text" :class="editUserData.loginInvalid" :disabled="editUserData.dialogMode !== 'ADD'" />
                        </div>
                    </div>
                    <div class="field grid">
                        <label class="col-12 md:col-4 md:mb-0">Описание (ФИО)</label>
                        <div class="col-12 md:col-8">
                            <InputText v-model="editUserData.displayName" type="text" />
                        </div>
                    </div>
                    <div class="field grid">
                        <label class="col-12 md:col-4 md:mb-0">E-mail</label>
                        <div class="col-12 md:col-8">
                            <InputText v-model="editUserData.email" type="text" />
                        </div>
                    </div>
                    <div class="field grid">
                        <label class="col-12 md:col-4 md:mb-0">Аватар</label>
                        <div class="col-12 md:col-5">
                            <img :src="editUserData.photo || '/noname.png'" width="32" style="vertical-align: middle" />
                        </div>
                        <div class="col-12 md:col-3">
                            <Button label="загрузить..." />
                        </div>
                    </div>
                    <div class="field grid">
                        <label class="col-12 md:col-4 md:mb-0">Пароль</label>
                        <div class="col-12 md:col-8">
                            <Password v-model="editUserData.password" :class="editUserData.passwordInvalid" />
                        </div>
                    </div>
                    <div class="field grid">
                        <label class="col-12 md:col-4 md:mb-0">Пароль подтверждение</label>
                        <div class="col-12 md:col-8">
                            <Password v-model="editUserData.passwordСonfirm" :class="editUserData.passwordСonfirmInvalid" />
                        </div>
                    </div>
                    <div class="field grid">
                        <label class="col-12 md:col-4 md:mb-0">Администратор</label>
                        <div class="col-12 md:col-8">
                            <Checkbox v-model="editUserData.isAdmin" :binary="true" :disabled="editUserData.dialogMode !== 'ADD'" />
                        </div>
                    </div>
                </div>
            </TabPanel>
            <TabPanel header="Сущности">
                <Textarea v-model="editUserData.entitiePermissions" style="resize: none; width: 100%; height: 55vh;" />
            </TabPanel>
            <TabPanel header="Дашборды">
                <Textarea v-model="editUserData.dasboardPermissions" style="resize: none; width: 100%; height: 55vh;" />
            </TabPanel>
        </TabView>

        <template #footer>
            <Button :style="{ width: '6rem' }" label="Ok" @click="onUserDialogOk()" autofocus />
            <Button :style="{ width: '6rem' }" label="Отмена" @click="onUserDialogCancel()" severity="danger" />
        </template>
    </Dialog>


    <Dialog v-model:visible="passwordDialogVisible" modal header="Header" :style="{ width: '30rem' }">
        <template #header>
            <span class="font-bold white-space-nowrap">Смена пароля для "{{ editUserData.login }}"</span>
        </template>
        <div class="p-fluid px-4">
            <Message v-if="editUserData.passwordInvalid !== ''" severity="error" :closable="false">
                Неправильный пароль или не совпадает подтверждение
            </Message>

            <div class="pb-2">
                <label>Новый пароль</label>
            </div>
            <Password v-model="editUserData.password" />

            <div class="pt-3 pb-2">
                <label>Пароль подтверждение</label>
            </div>
            <Password v-model="editUserData.passwordСonfirm" />
        </div>
        <template #footer>
            <Button :style="{ width: '6rem' }" label="Ok" @click="onPasswordDialogOk()" autofocus />
            <Button :style="{ width: '6rem' }" label="Отмена" @click="onPasswordDialogCancel()" />
        </template>
    </Dialog>
</template>

<style scoped>
</style>